// COP Copilot runtime — the OpenAI /v1 backend (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// LIFTED VERBATIM from takbot services/agent-runtime/src/backends/openai.ts (import sources
// changed: @takbot/protocol → ../types.ts, ../prompt.ts, ../context.ts, ../config.ts). This is
// TDFBot's PRESERVED OpenAI-compatible /v1 path: same {model,messages,temperature,seed,max_tokens}
// envelope, same response parsing (choices[0].message.content + reasoning_content fallback), raw
// fetch (no SDK). It drives the MCP tool loop: ONE grammar-constrained JSON object per step —
// {capability,args} or {final}.
//
// BACKEND TARGET (the COP decision): a LOCAL OpenAI /v1 server (llama.cpp llama-server or
// LM Studio) running the eval-winning Qwen3.6. Config via env:
//   TAKBOT_LLM_BASE_URL  — the local server, e.g. http://127.0.0.1:8081/v1
//   TAKBOT_LLM_MODEL     — defaults to unsloth/Qwen3.6-35B-A3B-GGUF (Will's bench: 90%, 3.8s,
//                          8/8 evac 0-leaks, best tool-chain).
// Health is GET {baseUrl}/models (the same probe TDFBot's _endpoint_ok used). On any HTTP error
// the loop's selectBackend (index.ts) falls back to the mock — the COP never hard-depends on a
// running model (browser AND the build/tests run fine with no server).
//
// CONFINEMENT: this backend only ever receives the system prompt, the user query, and the [n]
// tool-RESULT previews assembled by context.ts (describeResult) — it is NEVER handed a raw
// envelope, a sealed/decrypted payload, or a denied-object count. fetch() egress goes ONLY to the
// configured local /v1 base URL.

import type {
  Citation,
  ContextWindowConfig,
  FinalAnswer,
  McpManifest,
  ToolCall,
} from "../types.ts";
import type { Backend, Plan, SessionState } from "./types.ts";
import { pairSteps } from "./types.ts";
import {
  buildToolList,
  buildUserPrompt,
  REASONING_SUFFIX,
  COMMIT_INSTRUCTION,
} from "../prompt.ts";
import { formatToolResultsForContext } from "../context.ts";
import { getSystemPrompt, constrainEnabled } from "../config.ts";
import { readEnv } from "../env.ts";

/** One OpenAI chat message. */
interface ChatMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

/** Default model id when TAKBOT_LLM_MODEL is unset — the eval-winning local go-to. */
const DEFAULT_MODEL = "unsloth/Qwen3.6-35B-A3B-GGUF";

/** The per-step output JSON-Schema: the model is grammar-CONSTRAINED to emit EITHER a tool call
 *  ({capability ∈ the live manifest's ids, reason, args}) OR a final answer ({final:{...}}) —
 *  nothing else. llama.cpp / LM Studio convert this to a GBNF grammar at the sampler. The
 *  capability `enum` also stops the model inventing tool names that don't exist. */
export function buildStepSchema(manifest: McpManifest): Record<string, unknown> {
  const names = (manifest.tools ?? []).map((t) => t.name);
  const toolCall = {
    type: "object",
    properties: {
      capability: names.length > 0 ? { type: "string", enum: names } : { type: "string" },
      reason: { type: "string" },
      args: { type: "object" },
    },
    required: ["capability", "args"],
    additionalProperties: false,
  };
  const finalAnswer = {
    type: "object",
    properties: {
      final: {
        type: "object",
        properties: {
          text: { type: "string" },
          citations: {
            type: "array",
            items: {
              type: "object",
              properties: {
                ref: { type: "string" },
                index: { type: "number" },
                label: { type: "string" },
              },
              required: ["ref"],
            },
          },
          marking: { type: "string" },
          coverage_warning: { type: "string" },
        },
        required: ["text"],
      },
    },
    required: ["final"],
    additionalProperties: false,
  };
  return { anyOf: [toolCall, finalAnswer] };
}

/** The FINAL-only schema — used by finalize() to force a {final} answer (no tool-call branch). */
export function buildFinalSchema(): Record<string, unknown> {
  return {
    type: "object",
    additionalProperties: false,
    required: ["final"],
    properties: {
      final: {
        type: "object",
        required: ["text"],
        properties: {
          text: { type: "string" },
          citations: {
            type: "array",
            items: {
              type: "object",
              required: ["ref"],
              properties: {
                ref: { type: "string" },
                index: { type: "number" },
                label: { type: "string" },
              },
            },
          },
          marking: { type: "string" },
          coverage_warning: { type: "string" },
        },
      },
    },
  };
}

export class OpenAIBackend implements Backend {
  readonly name = "openai" as const;
  readonly model: string;
  readonly baseUrl: string;

  constructor(baseUrl: string, model: string = readEnv("TAKBOT_LLM_MODEL") || DEFAULT_MODEL) {
    this.baseUrl = baseUrl.replace(/\/+$/, "");
    this.model = model;
  }

  /** Health probe, mirroring TDFBot _endpoint_ok: GET {base}/models must 200 with >=1 model. */
  static async endpointOk(baseUrl: string): Promise<boolean> {
    const base = baseUrl.replace(/\/+$/, "");
    try {
      const resp = await fetch(`${base}/models`, {
        method: "GET",
        signal: AbortSignal.timeout(3000),
      });
      if (!resp.ok) return false;
      const json = (await resp.json()) as { data?: unknown };
      return Array.isArray(json.data) && json.data.length > 0;
    } catch {
      return false;
    }
  }

  async plan(state: SessionState): Promise<Plan> {
    const systemContent = getSystemPrompt() + buildToolList(state.manifest);
    const steps = pairSteps(state.transcript);
    const { context } = formatToolResultsForContext(steps, state.config);
    const userContent = (context ? context + "\n\n" : "") + buildUserPrompt(state.userText);

    const commitExtra: Record<string, unknown> = constrainEnabled()
      ? {
          response_format: {
            type: "json_schema",
            json_schema: { name: "takbot_step", schema: buildStepSchema(state.manifest) },
          },
          chat_template_kwargs: { enable_thinking: false },
        }
      : {};

    let reply: string;
    if (state.config.reasoning_mode === "think") {
      // ── PHASE A — free reasoning (no grammar; the model's think channel is welcome) ──────
      const reasoning = await this.chat(
        [
          { role: "system", content: systemContent + REASONING_SUFFIX },
          { role: "user", content: userContent },
        ],
        state.config,
        {},
      );
      // ── PHASE B — constrained commit, conditioned on the model's own reasoning ───────────
      reply = await this.chat(
        [
          { role: "system", content: systemContent },
          { role: "user", content: userContent },
          { role: "assistant", content: reasoning || "(no reasoning produced)" },
          { role: "user", content: COMMIT_INSTRUCTION },
        ],
        state.config,
        commitExtra,
      );
    } else {
      // ── instant — one grammar-constrained call, no think phase ───────────────────────────
      reply = await this.chat(
        [
          { role: "system", content: systemContent },
          { role: "user", content: userContent },
        ],
        state.config,
        commitExtra,
      );
    }

    return parseReply(reply, state.turn_id, steps.length);
  }

  /** One chat/completions call → the reply text. `extra` carries optional response_format /
   *  chat_template_kwargs. Mirrors TDFBot's request envelope, raw fetch, no SDK. */
  private async chat(
    messages: ChatMessage[],
    config: ContextWindowConfig,
    extra: Record<string, unknown>,
  ): Promise<string> {
    const resp = await fetch(`${this.baseUrl}/chat/completions`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        model: this.model,
        messages,
        temperature: config.temperature,
        seed: config.seed,
        max_tokens: config.max_tokens,
        ...extra,
      }),
      signal: AbortSignal.timeout(600_000),
    });
    if (!resp.ok) {
      throw new Error(`openai backend: chat/completions returned ${resp.status} ${resp.statusText}`);
    }
    const json = (await resp.json()) as {
      choices?: Array<{ message?: { content?: string | null; reasoning_content?: string | null } }>;
    };
    const message = json.choices?.[0]?.message ?? {};
    return (message.content && message.content.trim()) || message.reasoning_content || "";
  }

  /** Force a grounded {final} from the results already gathered. One constrained call against the
   *  FINAL-only schema, no think phase. */
  async finalize(state: SessionState): Promise<FinalAnswer> {
    const systemContent = getSystemPrompt() + buildToolList(state.manifest);
    const steps = pairSteps(state.transcript);
    const { context } = formatToolResultsForContext(steps, state.config);
    const userContent =
      (context ? context + "\n\n" : "") +
      `You must STOP calling tools and ANSWER NOW, using ONLY the tool results above.\n` +
      `REQUEST: ${state.userText}\n` +
      `Emit EXACTLY ONE {"final":{"text":"...","citations":[...]}} object — cite the [n] results, ` +
      `state confidence, carry the classification marking the tools returned, and say so if the picture is LIMITED.`;

    const extra: Record<string, unknown> = constrainEnabled()
      ? {
          response_format: {
            type: "json_schema",
            json_schema: { name: "takbot_final", schema: buildFinalSchema() },
          },
          chat_template_kwargs: { enable_thinking: false },
        }
      : {};

    const reply = await this.chat(
      [
        { role: "system", content: systemContent },
        { role: "user", content: userContent },
      ],
      state.config,
      extra,
    );

    const obj = extractFirstJsonObject(stripFences(reply));
    if (obj && typeof obj === "object" && (obj as Record<string, unknown>).final) {
      return coerceFinal((obj as Record<string, unknown>).final as Record<string, unknown>);
    }
    return {
      text:
        (reply && reply.slice(0, 1200)) ||
        "Unable to synthesize an answer from the available results. This answer is LIMITED.",
      citations: [],
      coverage_warning: "Synthesized under a forced stop; treat this as LIMITED, not a definitive negative.",
    };
  }
}

/** Parse the model's single-JSON-object reply into a Plan. */
export function parseReply(reply: string, turnId: string, completedSteps: number): Plan {
  const obj = extractFirstJsonObject(stripFences(reply));

  if (obj && typeof obj === "object") {
    const rec = obj as Record<string, unknown>;

    if (rec.final && typeof rec.final === "object") {
      const final = coerceFinal(rec.final as Record<string, unknown>);
      return { kind: "final", final };
    }

    const capability = typeof rec.capability === "string" ? rec.capability : undefined;
    if (capability) {
      const call: ToolCall = {
        call_id: `${turnId}-call-${completedSteps + 1}`,
        capability,
        reason: typeof rec.reason === "string" ? rec.reason : "",
        args: rec.args ?? {},
      };
      return { kind: "tool_calls", tool_calls: [call] };
    }
  }

  return {
    kind: "final",
    final: {
      text:
        "The model did not return a parseable tool call or final answer. This answer is LIMITED. " +
        "Confidence: low.",
      citations: [],
      coverage_warning:
        "No grounded tool results were obtained; treat this as a non-answer, not a definitive negative.",
    },
  };
}

/** Coerce a loosely-typed {final} object into a FinalAnswer, tolerating missing fields. */
function coerceFinal(raw: Record<string, unknown>): FinalAnswer {
  const text = typeof raw.text === "string" ? raw.text : "";
  const citations: Citation[] = [];
  if (Array.isArray(raw.citations)) {
    for (const c of raw.citations) {
      if (c && typeof c === "object") {
        const cr = c as Record<string, unknown>;
        citations.push({
          ref: typeof cr.ref === "string" ? cr.ref : String(cr.ref ?? ""),
          index: typeof cr.index === "number" ? cr.index : citations.length + 1,
          label: typeof cr.label === "string" ? cr.label : undefined,
        });
      }
    }
  }
  const final: FinalAnswer = { text, citations };
  if (typeof raw.marking === "string") final.marking = raw.marking;
  if (typeof raw.coverage_warning === "string") final.coverage_warning = raw.coverage_warning;
  return final;
}

/** Strip ```json / ``` code fences from a reply. */
function stripFences(s: string): string {
  return s.replace(/```[a-zA-Z]*\s*/g, "").replace(/```/g, "");
}

/** Extract the FIRST balanced top-level JSON object from a string, ignoring braces inside
 *  string literals. Returns the parsed value or undefined. */
export function extractFirstJsonObject(s: string): unknown {
  const start = s.indexOf("{");
  if (start < 0) return undefined;
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < s.length; i++) {
    const ch = s[i];
    if (inString) {
      if (escape) {
        escape = false;
      } else if (ch === "\\") {
        escape = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }
    if (ch === '"') {
      inString = true;
    } else if (ch === "{") {
      depth += 1;
    } else if (ch === "}") {
      depth -= 1;
      if (depth === 0) {
        const candidate = s.slice(start, i + 1);
        try {
          return JSON.parse(candidate);
        } catch {
          return undefined;
        }
      }
    }
  }
  return undefined;
}
